'use client'

import { useEffect, useRef, useState } from 'react'
import { type MusicTherapyJob } from '@/lib/music-therapy-job-format'
import { formatJobRegion, getJobDateMeta, getJobTitleColorClass } from '@/lib/job-board-presentation'

const ROW_COLUMNS = 'lg:grid-cols-[72px_minmax(0,2.6fr)_0.7fr_0.8fr_100px]'
const PAGE_SIZE = 15

function JobMeta({ job }: { job: MusicTherapyJob }) {
  const dateMeta = getJobDateMeta(job)

  return <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-[11px] font-bold leading-4 text-[#D1D5DB] sm:text-xs">
    <span className="inline-flex max-w-full items-center rounded-full border border-[#4B5563] bg-[#252525] px-2.5 py-1">
      <span className="truncate">{formatJobRegion(job.region)}</span>
    </span>
    <span className="inline-flex items-center rounded-full border border-[#4B5563] bg-[#252525] px-2.5 py-1 whitespace-nowrap">
      <span className="mr-1 text-[#FFB199]">{dateMeta.label}</span>
      {dateMeta.value}
    </span>
  </div>
}

export function JobBoardList({ jobs }: { jobs: MusicTherapyJob[] }) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        setVisibleCount((count) => Math.min(count + PAGE_SIZE, jobs.length))
      }
    }, { rootMargin: '200px' })
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [jobs.length])

  const visibleJobs = jobs.slice(0, visibleCount)

  return <>
    <div className={`hidden gap-4 border-b border-[#3A3A3A] bg-[#111827] px-5 py-3 text-xs font-black uppercase tracking-[0.12em] text-[#FFB199] lg:grid ${ROW_COLUMNS}`}>
      <span>번호</span>
      <span>지역 · 날짜 · 제목</span>
      <span>구분</span>
      <span>출처</span>
      <span className="text-right">링크</span>
    </div>
    <ul className="divide-y divide-[#3A3A3A]" aria-label="취업정보 리스트">
      {visibleJobs.map((job, index) => <li key={job.id} className={`grid min-w-0 gap-3 px-4 py-4 transition hover:bg-[#222222] sm:px-5 lg:items-center lg:gap-4 ${ROW_COLUMNS}`}>
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="inline-flex h-7 min-w-7 shrink-0 items-center justify-center rounded-full bg-[#2A2A2A] px-2 text-xs font-black text-[#F3F4F6]">{jobs.length - index}</span>
          <div className="min-w-0 lg:hidden"><JobMeta job={job} /></div>
        </div>
        <div className="min-w-0">
          <div className="hidden lg:block"><JobMeta job={job} /></div>
          <p className={`mt-1.5 min-w-0 whitespace-normal [overflow-wrap:anywhere] text-[15px] font-black leading-[1.55] sm:text-base lg:mt-2 ${getJobTitleColorClass(job)}`} title={job.title}>{job.title}</p>
        </div>
        <div className="flex min-w-0 items-center justify-between gap-3 lg:contents">
          <div className="min-w-0 text-sm font-bold text-[#D1D5DB]"><span className="mr-2 text-xs font-black text-[#FFB199] lg:hidden">구분</span><span className="break-words">{job.category}</span></div>
          <div className="min-w-0 text-right text-sm font-bold text-[#D1D5DB] lg:text-left"><span className="mr-2 text-xs font-black text-[#FFB199] lg:hidden">출처</span><span className="break-words">{job.source}</span></div>
        </div>
        <div className="flex justify-end lg:justify-self-end">
          <a href={job.url} target="_blank" rel="noopener noreferrer" className="inline-flex rounded-full bg-[#FF4500] px-4 py-2 text-xs font-black text-white transition hover:bg-[#CC3700]">원문 보기</a>
        </div>
      </li>)}
    </ul>
    {visibleCount < jobs.length ? <div ref={sentinelRef} className="py-6 text-center text-xs font-bold text-[#9CA3AF]">더 불러오는 중...</div> : null}
  </>
}
